import {
    Alert,
    Box,
    Grid,
    Chip,
    Snackbar,
    TextField,
    Typography,
    FormLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Skeleton,
    useMediaQuery,
} from '@mui/material'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import OccupancyEmpty from '@/assets/img/realtime/occupancy_empty.png'
import OccupancyFull from '@/assets/img/realtime/occupancy_full.png'
import { electricBlue, textBlueLight } from '@/components/Basic/colors'
import TemperatureInput from '@/components/form/temperature.component'
import AgridToggle from '@/components/Global/toggle.component'
import { getDataPoint, getTimeSeries } from '@/services/sensors.service'
import { hexToRGB } from '@/utils/global.utils'
import FanComponent from './fan.component'
import SaveMenu from '../../components/save-menu.component'
import ThermostatIcon from '@mui/icons-material/Thermostat'
import { useTranslation } from 'react-i18next'
import { useRealtime } from '../../useRealtime'

// The ambient temperature (InletTemp) current value now comes from the family-tree
// snapshot exposed through the realtime context. Its sensor id is the slow part of the
// getDataPoint query, so we keep that data type out of the request and only reuse its
// sensor ids to load the temperature time series chart.
const TEMPERATURE_DATA_TYPE_ID = 184

const getSensorGroupIds = (sensorGroup) =>
    Array.isArray(sensorGroup?.sensors) && sensorGroup.sensors.length > 0
        ? sensorGroup.sensors.filter(Boolean)
        : sensorGroup?.SensorId
          ? [sensorGroup.SensorId]
          : []

const getTranslatedModeName = (name, t) => {
    if (!name) {
        return ''
    }
    const key = String(name).trim().toUpperCase()
    return t(`modes.${key}`, { defaultValue: name })
}

const Telecommand = ({ sensors, addModification, objectId, saveModifications, items, hash, equipments }) => {
    const { t } = useTranslation('site', { keyPrefix: 'realtime.objectTelecommand' })
    const { parsedObjects } = useRealtime()
    const [loading, setLoading] = useState(true)
    const [tempOrder, setTempOrder] = useState(null)
    const [mode, setMode] = useState(null)
    const [drive, setDrive] = useState(null)
    const [fanSpeed, setFanSpeed] = useState(null)
    const [timeSeries, setTimeSeries] = useState(null)
    const [timeSeriesLoading, setTimeSeriesLoading] = useState(false)
    const [humidity, setHumidity] = useState(null)
    const [occupancy, setOccupancy] = useState(null)
    const [showAlert, setShowAlert] = useState()
    const [min, setMin] = useState(0)
    const [max, setMax] = useState(30)
    const [hasBreezePlugin, setHasBreezePlugin] = useState(false)
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'))

    const currentObject = parsedObjects?.find((el) => String(el?.objectId) === String(objectId))
    const temperature =
        currentObject?.temperature !== undefined && currentObject?.temperature !== null
            ? `${currentObject.temperature} °C`
            : null

    useEffect(() => {
        fetchData()
    }, [])
    const getLatestEntryDate = (data) => {
        if (!Array.isArray(data) || data.length === 0) {
            return null
        }
        let latest = null
        for (let i = 0; i < data.length; i++) {
            const date = data[i]?.DateTime ? new Date(data[i].DateTime) : null
            if (!date) {
                continue
            }
            if (!latest || date > latest) {
                latest = date
            }
        }
        return latest
    }

    const selectMostRecentMeasurement = (measurements) => {
        if (!measurements || measurements.length === 0) {
            return null
        }
        let latestMeasurement = measurements[0]
        let latestDate = getLatestEntryDate(latestMeasurement?.data)

        for (let i = 1; i < measurements.length; i++) {
            const candidate = measurements[i]
            const candidateDate = getLatestEntryDate(candidate?.data)

            if (!latestDate && candidateDate) {
                latestMeasurement = candidate
                latestDate = candidateDate
                continue
            }

            if (candidateDate && latestDate && candidateDate > latestDate) {
                latestMeasurement = candidate
                latestDate = candidateDate
            }
        }

        return latestMeasurement
    }

    const getCurrentSensor = (sensors, datatype) => {
        const items = sensors?.filter((el) => el?.DataTypeId === datatype) || []

        if (items.length === 1) {
            return items[0]
        }

        if (items.length > 1) {
            const candidates = items.filter((item) => Array.isArray(item?.data) && item.data.length > 0)

            if (candidates.length === 0) {
                return null
            }

            return selectMostRecentMeasurement(candidates)
        }

        return null
    }

    const fetchData = async () => {
        let sensors = []
        for (let i = 0; i < Object.keys(equipments).length; i++) {
            let equipment = Object.values(equipments)[i]
            sensors.push(...equipment.Sensors)
        }

        // Ambient temperature sensor ids are still needed for the time series chart, but
        // their current value comes from the snapshot, so they are excluded from getDataPoint.
        const temperatureSensorIds = Array.from(
            new Set(
                sensors
                    .filter((sensor) => sensor?.DataTypeId === TEMPERATURE_DATA_TYPE_ID)
                    .flatMap(getSensorGroupIds)
            )
        )

        if (sensors?.length > 0) {
            const sensorIds = Array.from(
                new Set(
                    sensors
                        .filter((sensor) => sensor?.DataTypeId !== TEMPERATURE_DATA_TYPE_ID)
                        .flatMap(getSensorGroupIds)
                )
            )
            if (sensorIds.length > 0) {
                const res = await getDataPoint({
                    hash,
                    sensors: sensorIds,
                    objectId,
                })

                setHasBreezePlugin(
                    res?.plugins?.some((plugin) => String(plugin).toLowerCase().includes('breeze')) ?? false
                )

                for (let i = 0; i < sensors?.length; i++) {
                    const sensorGroup = sensors[i]
                    const groupSensorIds = getSensorGroupIds(sensorGroup)

                    const measurementCandidates = groupSensorIds
                        .map((sensorId) => res?.measurement?.find((el) => el.sensorId === sensorId))
                        .filter(Boolean)

                    const measurement = selectMostRecentMeasurement(measurementCandidates)

                    if (measurement) {
                        sensors[i] = { ...sensorGroup, ...measurement }
                    }
                }
            }

            let item = getCurrentSensor(sensors, 226)
            if (item?.data?.length > 0) setTempOrder(item)

            item = getCurrentSensor(sensors, 225)
            if (item?.data?.length > 0) setMode(item)

            item = getCurrentSensor(sensors, 228)
            if (item?.data?.length > 0) setDrive(item)

            item = getCurrentSensor(sensors, 255)
            if (item?.data?.length > 0) {
                item.options = item?.options?.sort((a, b) => a.IdGlobal - b.IdGlobal)
                setFanSpeed(item)
            }

            item = getCurrentSensor(sensors, 313)
            if (item?.data?.length > 0) {
                setOccupancy(item)
            }

            item = getCurrentSensor(sensors, 175)
            if (item?.data?.length > 0) {
                setHumidity(item)
            }
        }
        setLoading(false)

        if (temperatureSensorIds.length > 0) {
            setTimeSeriesLoading(true)
            try {
                let yesterday = new Date()
                yesterday = yesterday.setDate(yesterday.getDate() - 3)
                const res = await getTimeSeries({
                    sensors: [...temperatureSensorIds],
                    hash,
                    StartDateTime: yesterday,
                })
                if (res?.measurements?.length > 0) {
                    setTimeSeries(res?.measurements)
                } else {
                    setTimeSeries([])
                }
            } finally {
                setTimeSeriesLoading(false)
            }
        } else {
            setTimeSeriesLoading(false)
        }
    }

    useEffect(() => {
        if (timeSeries?.length > 0) {
            let minValue = Math.min(...timeSeries.map((el) => parseFloat(el.Value)))
            let maxValue = Math.max(...timeSeries.map((el) => parseFloat(el.Value)))
            let diff = maxValue - minValue
            let margin = 0.01
            let minWithMargin = minValue - Math.min(minValue * margin, diff / 2)
            let maxWithMargin = maxValue + Math.min(maxValue * margin, diff / 2)
            setMin(minWithMargin)
            setMax(maxWithMargin)
        }
    }, [timeSeries])

    const value = drive?.data?.[0]?.Value === 1 ? 1 : 0
    const isOn = drive?.options?.find((el) => el?.IdGlobal === value)?.Name?.toLowerCase() === 'on'
    if (loading) {
        return (
            <>
                <Typography variant="h2" sx={{ mb: 4 }}>
                    <Skeleton width="60%" />
                </Typography>
                <Skeleton variant="rectangular" height={500} />
            </>
        )
    }

    return (
        <div style={{ position: 'relative' }}>
            {showAlert && (
                <Snackbar
                    style={{ zIndex: 1000 }}
                    open={showAlert}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    autoHideDuration={6000}
                    onClose={() => setShowAlert(null)}
                >
                    <Alert severity={showAlert}>
                        {showAlert === 'warning' ? t('feedback.error') : t('feedback.success')}
                    </Alert>
                </Snackbar>
            )}

            <Box>
                <Grid container alignItems="flex-start" sx={{ mb: 4 }} spacing={2} justifyContent="space-between">
                    <Grid
                        item
                        container
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={3}
                        xs={12}
                        md={10}
                        lg={8}
                    >
                        <Grid item xs={4} md={2} lg={1}>
                            {drive && (
                                <AgridToggle
                                    checked={isOn}
                                    onChange={(val) => {
                                        setDrive({
                                            ...drive,
                                            data: [
                                                {
                                                    ...drive?.data[0],
                                                    Value: val,
                                                },
                                            ],
                                        })
                                        addModification(drive?.actuator?.ActuatorId, val, '', drive?.sensors)
                                    }}
                                    name="status"
                                    styles={{ toggle: { margin: 0 }, label: { color: textBlueLight } }}
                                    disabled={!drive?.actuator}
                                />
                            )}
                        </Grid>
                        {isMobile && (
                            <Grid item xs={8} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <SaveMenu
                                    title={t('actions.save')}
                                    saveModifications={saveModifications}
                                    items={items}
                                />
                            </Grid>
                        )}

                        {mode?.data?.[0]?.Value !== undefined && mode?.data?.[0]?.Value !== null ? (
                            <Grid item xl={1} md={2} sm={3} xs={6}>
                                {hasBreezePlugin && mode?.actuator ? (
                                    <FormControl variant="standard" style={{ width: '100%' }}>
                                        <InputLabel id="mode-select-label" style={{ color: textBlueLight }}>
                                            {t('fields.mode')}
                                        </InputLabel>
                                        <Select
                                            labelId="mode-select-label"
                                            id="mode-select"
                                            value={mode?.data?.[0]?.Value}
                                            label={t('fields.mode')}
                                            onChange={(e) => {
                                                const value = e.target.value
                                                setMode({
                                                    ...mode,
                                                    data: [{ ...mode?.data[0], Value: value }],
                                                })
                                                addModification(
                                                    mode?.actuator?.ActuatorId,
                                                    value,
                                                    '',
                                                    mode?.sensors,
                                                    225
                                                )
                                            }}
                                        >
                                            {[...(mode?.options ?? [])]
                                                .sort((a, b) => a.IdGlobal - b.IdGlobal)
                                                .map((option) => (
                                                    <MenuItem key={option.IdGlobal} value={option.IdGlobal}>
                                                        {getTranslatedModeName(option.Name, t)}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <TextField
                                        InputLabelProps={{
                                            style: { color: textBlueLight },
                                        }}
                                        id="outlined-basic"
                                        label={t('fields.mode')}
                                        variant="standard"
                                        value={getTranslatedModeName(
                                            mode?.options?.find((el) => el?.IdGlobal === mode?.data?.[0]?.Value)
                                                ?.Name,
                                            t
                                        )}
                                        disabled
                                        style={{ width: '100%' }}
                                    />
                                )}
                            </Grid>
                        ) : null}
                        {tempOrder?.data?.[0]?.Value ? (
                            <Grid item>
                                <TemperatureInput
                                    min={tempOrder?.actuator?.Min}
                                    max={tempOrder?.actuator?.Max}
                                    onChange={(value) => {
                                        if (
                                            mode?.options?.find((el) => el?.IdGlobal === mode?.data?.[0]?.Value)
                                                ?.Name === 'FAN'
                                        )
                                            return
                                        setTempOrder({
                                            ...tempOrder,
                                            data: [{ ...tempOrder?.data[0], Value: value }],
                                        })
                                        addModification(tempOrder?.actuator?.ActuatorId, value, '', tempOrder.sensors)
                                    }}
                                    value={tempOrder?.data?.[0]?.Value || ''}
                                    unit={tempOrder?.Unit}
                                    label={t('fields.setpoint')}
                                    disabled={
                                        mode?.options?.find((el) => el?.IdGlobal == mode?.data?.[0]?.Value)?.Name ==
                                            'FAN' || !tempOrder?.actuator
                                    }
                                />
                                {tempOrder?.actuator ? (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            width: '100%',
                                        }}
                                    ></div>
                                ) : null}
                            </Grid>
                        ) : null}
                        {fanSpeed && fanSpeed?.data?.[0]?.Value !== undefined && fanSpeed?.data?.[0]?.Value !== null ? (
                            <Grid item sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <FormLabel style={{ fontSize: '12px', color: textBlueLight }}>
                                    {t('fields.fanSpeed')}
                                </FormLabel>
                                <FanComponent
                                    fanSpeed={fanSpeed}
                                    disabled={!fanSpeed?.actuator}
                                    onClick={(value) => {
                                        setFanSpeed({
                                            ...fanSpeed,
                                            data: [
                                                {
                                                    ...fanSpeed?.data[0],
                                                    Value: value,
                                                },
                                            ],
                                        })
                                        addModification(fanSpeed?.actuator?.ActuatorId, value, '', sensors, 255)
                                    }}
                                />
                            </Grid>
                        ) : null}

                        {occupancy?.data?.[0]?.Value !== undefined && occupancy?.data?.[0]?.Value !== null ? (
                            <Grid item xs={6} md={2}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.38)' }}>
                                        {t('fields.occupancy')}
                                    </Typography>
                                    <img
                                        src={occupancy?.data?.[0]?.Value ? OccupancyFull : OccupancyEmpty}
                                        style={{ width: 20 }}
                                    />
                                </div>
                            </Grid>
                        ) : null}
                        {humidity?.data?.[0]?.Value !== undefined && humidity?.data?.[0]?.Value !== null && (
                            <Grid item xs={6} md={2}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.38)' }}>
                                        {t('fields.humidity')}
                                    </Typography>
                                    {humidity?.data?.[0]?.Value}%
                                </div>
                            </Grid>
                        )}
                    </Grid>
                    {!isMobile && (
                        <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <SaveMenu title={t('actions.save')} saveModifications={saveModifications} items={items} />
                        </Grid>
                    )}
                </Grid>

                {temperature && (
                    <Box sx={{ mb: 2 }}>
                        <Chip
                            label={
                                <span>
                                    {t('ambiantTemperature')}&nbsp;&nbsp;<b>{temperature}</b>
                                </span>
                            }
                            icon={<ThermostatIcon fontSize="small" />}
                        />
                    </Box>
                )}

                {timeSeries?.length > 0 ? (
                    <HighchartsReact
                        highcharts={Highcharts}
                        options={{
                            chart: {
                                type: 'area',

                                zoomBySingleTouch: true,
                                zoomType: 'x',
                            },
                            legend: { enabled: false },
                            title: {
                                text: '',
                                enabled: false,
                            },
                            xAxis: {
                                type: 'datetime',
                                labels: {
                                    format: '{value:%d/%m, %Hh%M}',
                                    align: 'center',
                                },
                            },
                            yAxis: {
                                title: {
                                    text: `${t('chart.yAxisLabel')} (°C)`,
                                },
                                min: Math.min(min, 14),
                                max,
                                tooltip: {
                                    formatter: function () {
                                        return this.value + '°C'
                                    },
                                },
                            },
                            plotOptions: {
                                areaspline: {
                                    fillOpacity: 0.4,
                                },
                            },

                            tooltip: {
                                formatter: function () {
                                    return `<b>${this.y}°C</b>`
                                },
                            },

                            series: [
                                {
                                    name: 'Temp. mesurée',
                                    data: timeSeries?.map((el) => {
                                        let date = moment(el?.DateTime).toDate()
                                        date = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
                                        return {
                                            y: parseInt(parseFloat(el?.Value) * 10) / 10,
                                            x: date.getTime(), //el.DateTime,
                                        }
                                    }),
                                    hash: 4,
                                    visible: true,
                                    marker: {
                                        enabled: false,
                                    },
                                    type: 'area',
                                    spacingLeft: 10,

                                    color: {
                                        linearGradient: { x1: 0, x2: 1, y1: 0, y2: 1 },
                                        stops: [
                                            [0, electricBlue],
                                            [1, hexToRGB(electricBlue, 0.5)],
                                        ],
                                    },
                                },
                            ],
                        }}
                    />
                ) : timeSeriesLoading ? (
                    <Skeleton variant="rectangular" height={400} />
                ) : (
                    <Box>
                        <Alert severity="warning">{t('feedback.alert')}</Alert>
                    </Box>
                )}
            </Box>
        </div>
    )
}
export default Telecommand
